import { makeAutoObservable } from "mobx";
import type { SessionStore } from "../session-store";
import type {
  FeatureDescriptor,
  FeatureResolution,
  FeatureSurface,
  LoadedFeatureModule,
  ViewDeviceType,
  ViewScreenType,
} from "./feature-contract";

export type FeatureLoadResult =
  | { readonly status: "loaded"; readonly module: LoadedFeatureModule }
  | {
      readonly status: "denied" | "dependency-blocked" | "unknown" | "unsupported-surface";
    };

export class FeatureRegistryStore {
  private readonly descriptors: ReadonlyMap<string, FeatureDescriptor>;

  private device: ViewDeviceType;
  private screen: ViewScreenType;

  constructor(
    descriptors: readonly FeatureDescriptor[],
    private readonly session: SessionStore,
    device: ViewDeviceType = "desktop",
    screen: ViewScreenType = "main",
  ) {
    this.descriptors = new Map(descriptors.map((descriptor) => [descriptor.id, descriptor]));
    this.device = device;
    this.screen = screen;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Re-classifies device/screen after a live viewport change (e.g. the browser
   * window is resized or maximized after initial load). `resolve`/`discoverable`
   * read `this.device`/`this.screen` on every call, so an `observer` component
   * re-renders automatically once this updates — see `use-responsive-device.ts`.
   */
  setDeviceAndScreen(device: ViewDeviceType, screen: ViewScreenType): void {
    this.device = device;
    this.screen = screen;
  }

  get registeredCount(): number { return this.descriptors.size; }
  get blockedCount(): number {
    return [...this.descriptors.values()].filter(
      (descriptor) => this.resolve(descriptor.id).status !== "allowed",
    ).length;
  }

  list(): readonly FeatureDescriptor[] { return [...this.descriptors.values()]; }
  discoverable(): readonly FeatureDescriptor[] {
    return this.list().filter((descriptor) => this.resolve(descriptor.id).status === "allowed");
  }

  private hasDiscoveryPermission(descriptor: FeatureDescriptor): boolean {
    return descriptor.permissionMode === "any"
      ? this.session.canDiscoverAny(descriptor.requiredPermissions)
      : this.session.canDiscover(descriptor.requiredPermissions);
  }

  resolve(id: string, surface: FeatureSurface = "route"): FeatureResolution {
    const descriptor = this.descriptors.get(id);
    if (descriptor === undefined) return { status: "unknown" };
    if (descriptor.availability === "dependency-blocked") {
      return { descriptor, status: "dependency-blocked" };
    }
    if (!this.session.isViewAllowed(descriptor.id) ||
      !descriptor.policy.supportedDevices.includes(this.device) ||
      !descriptor.policy.supportedScreens.includes(this.screen) ||
      (descriptor.policy.featureFlag !== null &&
        this.session.featureFlags.get(descriptor.policy.featureFlag) !== true)) {
      return { descriptor, status: "denied" };
    }
    if (!descriptor.surfaces.includes(surface)) {
      return { descriptor, status: "unsupported-surface" };
    }
    if (!this.hasDiscoveryPermission(descriptor)) {
      return { descriptor, status: "denied" };
    }
    return { descriptor, status: "allowed" };
  }

  async load(id: string, surface: FeatureSurface): Promise<FeatureLoadResult> {
    const resolution = this.resolve(id, surface);
    if (resolution.status !== "allowed") return { status: resolution.status };
    return { module: await resolution.descriptor.load(), status: "loaded" };
  }
}
